import React, { useCallback, useState } from "react";
import {
  Alert,
  Box,
  Container,
  Grid,
  Snackbar,
  Typography,
} from "@mui/material";
import { Plus } from "lucide-react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Breadcrumb from "@components/Breadcrumb";
import HeroCst from "@components/HeroCst";
import BackgroundImageCst from "@components/BackgroundImageCst";
import ButtonBackPage from "@components/ButtonBackPage";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import AnimationFadeIn from "@components/AnimationFadeIn";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import { ButtonSecondary } from "src/UI/ButtonSecondary";
import { useFetchDetail } from "@hooks/useFetchDetail";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";

import { BrandAssetVariableResponse, CorporateBrandAssetDto } from "./types";
import { GLOBAL_CORPORATE_ID } from "./constants";
import { extractBrandAssetContent } from "./services";
import { useBrandAssets } from "./hooks/useBrandAssets";
import ModelCard from "./components/ModelCard";
import ModelEditorDialog from "./components/ModelEditorDialog";
import VariableEditorDialog from "./components/VariableEditorDialog";

const BrandAsset: React.FC = () => {
  const { t } = useTranslation(["corporate-brand-asset", "modal"]);
  const { id } = useParams();
  const corporateId = Number(id);
  const isGlobalScope = corporateId === GLOBAL_CORPORATE_ID;

  const { dataDetail: corporate } = useFetchDetail(
    ServicesURL.corporate,
    id || "",
    PagesID.corporate,
  );

  const { dataDetail: globalCorporate } = useFetchDetail(
    ServicesURL.corporate,
    isGlobalScope ? "" : `${GLOBAL_CORPORATE_ID}`,
    PagesID.corporate,
  );

  const corporateName = corporate?.name || "";
  const globalCorporateName = isGlobalScope
    ? corporateName
    : globalCorporate?.name || "";

  const {
    list,
    globalList,
    isLoading,
    mutate,
    createBrandAsset,
    editBrandAsset,
    deleteBrandAsset,
    createBrandAssetVariable,
  } = useBrandAssets(corporateId);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<"create" | "edit">("create");
  const [editingDto, setEditingDto] = useState<CorporateBrandAssetDto>();
  const [modelToDelete, setModelToDelete] = useState<CorporateBrandAssetDto>();
  const [variableEditorOpen, setVariableEditorOpen] = useState(false);
  const [existingVariableNames, setExistingVariableNames] = useState<string[]>(
    [],
  );
  const [variableFeedback, setVariableFeedback] = useState<string>();
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
  }>();

  const handleAdd = () => {
    setEditorMode("create");
    setEditingDto(undefined);
    setEditorOpen(true);
  };

  const handleEdit = (model: CorporateBrandAssetDto) => {
    setEditorMode("edit");
    setEditingDto(model);
    setEditorOpen(true);
  };

  const handleAddVariable = useCallback(async () => {
    setVariableEditorOpen(true);
    const [firstModel] = list;
    if (!firstModel) {
      setExistingVariableNames([]);
      return;
    }
    try {
      const content = await extractBrandAssetContent(firstModel);
      setExistingVariableNames(Object.keys(content.colors));
    } catch (error) {
      setExistingVariableNames([]);
      console.error(error);
    }
  }, [list]);

  const handleVariableCreated = useCallback(
    (response: BrandAssetVariableResponse) => {
      setVariableEditorOpen(false);
      mutate();
      setVariableFeedback(
        t("variables.created", {
          name: response.names?.length
            ? response.names.join(", ")
            : response.name,
          models: response.updatedModels,
          initiatives: response.updatedInitiatives,
        }),
      );
    },
    [mutate, t],
  );

  const handleSaved = useCallback(() => {
    setEditorOpen(false);
    mutate();
    setOpenToast({ s: StatusEnum.OK, message: "" });
  }, [mutate]);

  const handleConfirmDelete = useCallback(async () => {
    if (!modelToDelete) return;
    setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
    try {
      await deleteBrandAsset(modelToDelete.id);
      mutate();
      setModelToDelete(undefined);
      setOpenToast({ s: StatusEnum.OK, message: "" });
    } catch (error) {
      setModelToDelete(undefined);
      setOpenToast(undefined);
      console.error(error);
    }
  }, [modelToDelete, deleteBrandAsset, mutate]);

  return (
    <AnimationFadeIn>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-corporate.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container maxWidth={"xxl"} sx={{ position: "relative", zIndex: 1 }}>
        <HeroCst
          title={t(isGlobalScope ? "hero.titleGlobal" : "hero.titleDedicated")}
          description={t(
            isGlobalScope
              ? "hero.descriptionGlobal"
              : "hero.descriptionDedicated",
          )}
        />
        <ButtonBackPage />
        <Box
          sx={{
            mt: "25px",
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            mb: 2,
          }}
        >
          {isGlobalScope && (
            <ButtonSecondary
              id="brand-asset-add-variable"
              size="md"
              onClick={handleAddVariable}
              disabled={!corporateName}
              startIcon={<Plus size={16} />}
            >
              {t("actions.addVariable")}
            </ButtonSecondary>
          )}
          <ButtonPrimary
            id="brand-asset-add"
            size="md"
            onClick={handleAdd}
            disabled={!corporateName}
            startIcon={<Plus size={16} />}
          >
            {t("actions.add")}
          </ButtonPrimary>
        </Box>

        {!isLoading && list.length === 0 ? (
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            {t("emptyList")}
          </Typography>
        ) : (
          <Grid container spacing={3}>
            {list.map((model) => (
              <Grid item xs={12} sm={6} md={4} key={model.id}>
                <ModelCard
                  model={model}
                  onEdit={handleEdit}
                  onDelete={setModelToDelete}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {editorOpen && (
        <ModelEditorDialog
          open={editorOpen}
          mode={editorMode}
          editingDto={editingDto}
          corporateId={corporateId}
          isGlobalScope={isGlobalScope}
          sourceModels={globalList}
          corporateName={corporateName}
          globalCorporateName={globalCorporateName}
          createBrandAsset={createBrandAsset}
          editBrandAsset={editBrandAsset}
          onClose={() => setEditorOpen(false)}
          onSaved={handleSaved}
        />
      )}

      {variableEditorOpen && (
        <VariableEditorDialog
          open={variableEditorOpen}
          corporateId={corporateId}
          existingNames={existingVariableNames}
          createBrandAssetVariable={createBrandAssetVariable}
          onClose={() => setVariableEditorOpen(false)}
          onCreated={handleVariableCreated}
        />
      )}

      <ModalConfirmsCst
        open={!!modelToDelete}
        title={t("delete-item", { ns: "modal" })}
        description={t("confirm-delete", { ns: "modal" })}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setModelToDelete(undefined);
          setOpenToast(undefined);
        }}
        onActionConfirmed={handleConfirmDelete}
        status={openToast}
      />

      <Snackbar
        open={!!variableFeedback}
        autoHideDuration={6000}
        onClose={() => setVariableFeedback(undefined)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {variableFeedback ? (
          <Alert
            severity="success"
            onClose={() => setVariableFeedback(undefined)}
          >
            {variableFeedback}
          </Alert>
        ) : undefined}
      </Snackbar>
    </AnimationFadeIn>
  );
};

export default BrandAsset;
